import React, { useEffect, useState, useRef, useCallback } from "react";
import "./index.css";
import { Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchUserFollowers, fetchUserConnections, fetchCompanyFollowers, fetchUserFollowing, fetchPostLikes } from "../../redux/slice/allUsersSlice";
import { LocationIcon } from "../../assets/icons";
import withModal from "../withModal";
import { UserInital } from "../../utils/otherHelopers";
import { TableSearch } from "../form/Fields";

const UserNetworkingModalInner = ({ userId, type, onClose, setTitle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { networkingUsers, networkingLoading, networkingTotal } = useSelector(
    (state) => state.allUsers
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const lastFetchedPage = useRef(1);

  const getAction = useCallback((t) => {
    switch (t) {
      case "followers": return fetchUserFollowers;
      case "connections": return fetchUserConnections;
      case "following": return fetchUserFollowing;
      case "companyFollowers": return fetchCompanyFollowers;
      case "postLikes": return fetchPostLikes;
      default: return fetchUserFollowers;
    }
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle initial load and search changes
  useEffect(() => {
    if (userId && type) {
      setPage(1);
      setHasMore(true);
      lastFetchedPage.current = 1;

      const action = getAction(type);
      dispatch(action({ id: userId, page: 1, limit: 10, search: debouncedSearch })).then((res) => {
        if (res.payload?.data?.length < 10) {
          setHasMore(false);
        }
      });
    }
  }, [userId, type, dispatch, debouncedSearch, getAction]);

  const loadMore = useCallback(() => {
    const nextPage = page + 1;
    if (networkingLoading || !hasMore || lastFetchedPage.current >= nextPage) return;

    lastFetchedPage.current = nextPage;
    const action = getAction(type);
    dispatch(action({ id: userId, page: nextPage, limit: 10, search: debouncedSearch })).then((res) => {
      const newData = res.payload?.data || [];
      if (newData.length === 0) {
        setHasMore(false);
      } else {
        setPage(nextPage);
      }
    });
  }, [dispatch, userId, page, networkingLoading, hasMore, type, debouncedSearch, getAction]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !networkingLoading) {
          loadMore();
        }
      },
      { root: null, rootMargin: "20px", threshold: 1.0 }
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, networkingLoading, loadMore]);

  const title = {
    followers: "Followers",
    connections: "Connections",
    following: "Following",
    companyFollowers: "Company Followers",
    postLikes: "Post Likes"
  }[type] || "Users";

  return (
    <>
    <div className="p-3">
      <h4 className="h4 text-center fw-bold text-primary" style={{fontSize:"20px"}}>
        {title} ({networkingTotal})
      </h4>
    </div>
    
    

      <Modal.Body className="custom-scroll" style={{ minHeight: "400px", maxHeight: "60vh", padding: "20px 24px" }}>
        <TableSearch
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
          className="bg-light"
          coverClass="w-100 mw-100 mb-3"
        />
        {networkingUsers.length === 0 && !networkingLoading ? (
          <div className="text-center mt-5 text-muted">
            <p className="mb-0">No {title.toLowerCase()} found.</p>
            {debouncedSearch && <small>Try a different search term</small>}
          </div>
        ) : (
          <div className="d-flex flex-column">
            {networkingUsers.map((user) => {
              const firstName = user.first_name || "";
              const lastName = user.last_name || "";
              const isSameName = firstName.toLowerCase() === lastName.toLowerCase();
              const fullName = isSameName ? firstName : `${firstName} ${lastName}`;

              return (
                <div
                  key={user.id}
                  className="d-flex align-items-center justify-content-between p-3 networking-item"
                  onClick={() => {
                    if (params.id === user.id) {
                      onClose();
                    } else {
                      navigate(`/user/user-detail/${user.id}`);
                    }
                  }}
                >
                  <div className="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
                    <UserInital width={50} image={user.profile.profile_photo.url} name={fullName} />
                    
                    <div className="min-w-0">
                      <h6 className="mb-0 fw-semibold text-dark text-truncate" style={{ fontSize: "15px" }}>
                        {fullName}
                      </h6>
                      {(user.profession?.name || user.profile?.profession?.name) && (
                        <p className="mb-0 text-muted text-truncate mt-1" style={{ fontSize: "12.5px", opacity: 0.85 }}>
                          {user.profession?.name || user.profile?.profession?.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 ms-3 flex-shrink-0">
                    {(user.city?.name || user.profile?.city?.name) && (
                      <div className="d-flex align-items-center flex-shrink-0" style={{ gap: "6px" }}>
                        <div className="d-flex primary align-items-center justify-content-center rounded-circle" style={{ width: "24px", height: "24px", backgroundColor: "rgba(159, 47, 72, 0.08)" }}>
                          <LocationIcon size={12}/>
                        </div>
                        <span className="text-muted fw-semibold" style={{ fontSize: "13px", color: "#5e5873" }}>
                          {user.city?.name || user.profile?.city?.name}
                        </span>
                      </div>
                    )}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B9B9C3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ms-2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div ref={loaderRef} style={{ height: "1px" }}></div>
        {networkingLoading && (
          <div className="text-center mt-3">
            <Spinner animation="border" variant="danger" size="sm" style={{ opacity: 0.7 }} />
          </div>
        )}
      </Modal.Body>
    </>
  );
};

export default withModal(UserNetworkingModalInner);

